import { NotificationUpdateUserRequestV2 } from '@calm/evexia-web-sdk';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import createNotificationApi from '@rhg/libs/api/create-notification-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryKeys,
    useQuery,
    useMutation,
    queryClient,
} from '@rhg/libs/react-query';
import useAccount from '../use-account';
import { useIsSamlUser } from '../use-is-saml-user';

export function useNotificationUser() {
    const { isAuthenticated, getAccessTokenSilently, isLoading } = useAuth();
    const { account, isFetched } = useAccount();
    const isLinkedUser = useIsSamlUser();

    const { data, ...otherProps } = useQuery({
        queryKey: [queryKeys.notificationUser],
        queryFn: async () => {
            const notificationsApi = createNotificationApi(
                getAccessTokenSilently
            );

            const response = await notificationsApi.getUser({
                userId: account!.userId!,
            });
            return response.data;
        },
        enabled: !isLoading && isAuthenticated && isFetched && !isLinkedUser,
    });

    return {
        notificationUser: data,
        ...otherProps,
    };
}

export function useNotificationUserUpdate() {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle } = useActiveCircle();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (
            notificationUpdateUserRequestV2: NotificationUpdateUserRequestV2
        ) => {
            const api = createNotificationApi(getAccessTokenSilently);
            if (
                !activeCircle?.recipient?.userId ||
                !activeCircle?.authorizationJWT
            ) {
                return;
            }
            const response = await api.updateUserV2({
                userId: activeCircle.recipient.userId,
                circleAuthorizationToken: activeCircle?.authorizationJWT,
                notificationUpdateUserRequestV2,
            });
            return response.data;
        },
        onSuccess: (notificationUser) => {
            if (notificationUser) {
                queryClient.setQueryData(
                    [queryKeys.notificationUser],
                    notificationUser
                );
            }
        },
    });
    return {
        notificationUserUpdate: mutate,
        notificationUserUpdateAsync: mutateAsync,
        ...otherProps,
    };
}
